import {
    ActiveClusterStatus,
    ApplianceOverviewView,
    ApplianceSotwareName,
    ApplianceUpgradeStatus,
    ArrayStatus,
    autoContinues,
    CduAppliance,
    Check,
    CheckStatus,
    CloudPrecheck,
    CloudPrecheckUiName,
    ContractStatus,
    EdgeStatus,
    EdgeStatusLabel,
    EdgeStatusMessageWithIcon,
    InstallCheck,
    InstallCheckOverrideDurability,
    InstallUpgradeWorkflowInstance,
    LifecycleState,
    OnArrayPrecheckJob,
    Policy,
    PolicyVersionsModel,
    PolicyVersionData,
    Precheck,
    Release,
    ReplicationType,
    SoftwareType,
    SupportState,
    SupportStateShortcut,
    UpgradeCheckStatus,
    UpgradePoliciesModel,
    UpgradeWorkflowInstance,
    WorkflowCreateRequestType,
    WorkflowInstanceStatus,
    versionsDropdownItem,
} from './purity-upgrades.interface';
import moment from 'moment-timezone';
import { PartialDeep } from 'lodash';
import { UpgradePathRequiredInfo } from './services/releases.service';
import { EnableDisableEdgeServiceModalComponent } from '../purity-optimization/enable-disable-edge-service-modal/enable-disable-edge-service-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

const PURITY_UPGRADES_ANALYTICS_CATEGORY = 'Purity Upgrades';
const DEFAULT_RELEASE_NOTES_URL =
    'https://support.purestorage.com/FlashArray/FlashArray_Release/01_Purity_FA_Release_Notes';

export const SSU_TOKEN_KEY = 'SSU-TOKEN-KEY';

const UNSUPPORTED_REPLICATION_TYPES = [];

export const HEALTHCHECK_IN_PROGRESS_STATUSES: ApplianceUpgradeStatus[] = [
    ApplianceUpgradeStatus.HEALTH_CHECK_IN_PROGRESS,
];

export const HEALTHCHECK_SUCCESS_STATUSES: ApplianceUpgradeStatus[] = [
    ApplianceUpgradeStatus.HEALTH_CHECK_COMPLETED,
    ApplianceUpgradeStatus.HEALTH_CHECK_WARNING,
];

export const HEALTHCHECK_FAIL_STATUSES: ApplianceUpgradeStatus[] = [
    ApplianceUpgradeStatus.HEALTH_CHECK_FAILED,
    ApplianceUpgradeStatus.HEALTH_CHECK_ERROR,
    ApplianceUpgradeStatus.HEALTH_CHECK_FAILED_TO_START,
];

export const HEALTCHECK_STATUSES: ApplianceUpgradeStatus[] = [
    ...HEALTHCHECK_IN_PROGRESS_STATUSES,
    ...HEALTHCHECK_SUCCESS_STATUSES,
    ...HEALTHCHECK_FAIL_STATUSES,
];

export enum ACTION_REQUIRED_OPTIONS {
    RUN_HEALTH_CHECK = 'Run Health Check',
    DOWNLOAD_BUNDLE = 'Download Bundle',
    VIEW_RESULTS = 'View Results',
    REVIEW_TO_CONTINUE = 'Review to Continue',
    DOWNLOAD_AGAIN = 'Download Again',
    INSTALL_PURITY = 'Install Purity',
    ACTION_REQUIRED = 'Action Required',
    VIEW_DETAILS = 'View Details',
    SUPPORT_CASE = 'View Support Case',
    CREATE_SUPPORT_CASE = 'Open Support Case',
    SCHEDULE_ASSISTED_UPGRADE = 'Schedule Assisted Upgrade',
    ENABLE_PES = 'Enable PES (CLI)',
    INSTALL_UPGRADE_AGENT = 'Install Upgrade Agent',
    UPDATE_AGENT = 'Update Agent',
    VIEW_REPORT = 'View Report',
}

export interface ArrayCurrentStatus {
    icon?: string;
    status?: string;
    additionalInfo?: string;
    actionRequired?: ACTION_REQUIRED_OPTIONS;
    timestamp: moment.Moment | null;
    applianceId: string;
    includeTargetVersion?: boolean;
    shouldHideToast?: boolean;
    showCaseNumber?: boolean;
    isError?: boolean;
    viewDetails?: boolean;
    toastClass?: string;
    showScheduledUpgrade?: boolean;
    initialView?: ApplianceOverviewView;
}

export const upgradePolicies: UpgradePoliciesModel[] = [
    { code: Policy.BALANCED, displayName: 'Balanced' },
    { code: Policy.ESSENTIAL, displayName: 'Essential' },
    { code: Policy.PROACTIVE, displayName: 'Proactive' },
];

export const ONARRAY_PRECHECK_DISABLED_VERSIONS = ['6.4.9', '6.4.10']; // https://jira.purestorage.com/browse/CLOUD-108501

export const SAFEMODE_AUTO_ON_BOUNDARY_VERSION = '6.4.10';

/**
 * Compare that can be used for sorting releases descending
 *
 * Returns positive if rel1 < rel2
 * Returns negative if rel1 > rel2
 * Returns 0 if release version and time is the same.
 *
 * @param rel1
 * @param rel2
 */
export function compareReleases(rel1: Release, rel2: Release): number {
    if (rel1.versionMajor !== rel2.versionMajor) {
        return rel2.versionMajor - rel1.versionMajor;
    }

    if (rel1.versionMinor !== rel2.versionMinor) {
        return rel2.versionMinor - rel1.versionMinor;
    }

    if (rel1.versionPatch !== rel2.versionPatch) {
        return rel2.versionPatch - rel1.versionPatch;
    }
    return rel2.releaseTime.diff(rel1.releaseTime);
}

export function splitToChunks<T>(array: T[], chunkSize: number): T[][] {
    const result = [];

    for (let i = 0; i < array.length; i += chunkSize) {
        const end = i + chunkSize > array.length ? array.length : i + chunkSize;
        result.push(array.slice(i, end));
    }

    return result;
}

export function getUnsupportedReplicationNamesPresentOnAppliance(appliance: CduAppliance): string {
    const unsupportedReplicationTypes = getDistinctReplicationTypesOnAppliance(appliance)
        .filter(type => UNSUPPORTED_REPLICATION_TYPES.includes(type))
        .map(translateReplicationType);

    return unsupportedReplicationTypes.join(' and ');
}

function getDistinctReplicationTypesOnAppliance(appliance: CduAppliance): ReplicationType[] {
    const distinctReplicationTypes = new Set<ReplicationType>();
    appliance?.replications?.forEach(replication => distinctReplicationTypes.add(replication.type));
    return [...distinctReplicationTypes];
}

function translateReplicationType(replicationType: ReplicationType): string {
    switch (replicationType) {
        case ReplicationType.ACTIVE_DR:
            return 'ActiveDR';
        case ReplicationType.ACTIVE_CLUSTER:
            return 'ActiveCluster';
        default:
            return '';
    }
}

export function noUnsupportedReplicationsExist(arrayStatus: ArrayStatus): boolean {
    return !(
        arrayStatus?.replications?.find(replication => UNSUPPORTED_REPLICATION_TYPES.includes(replication.type)) ??
        false
    );
}

export function noUnsupportedReplicationsExistOnAppliance(appliance: CduAppliance): boolean {
    return !(
        appliance?.replications?.find(replication => UNSUPPORTED_REPLICATION_TYPES.includes(replication.type)) ?? false
    );
}

/**
 * Prechecks have no signalisation that they were not even executed due to an error
 * Because of that valid completed precheck is a precheck that is FINALIZED
 * and has at least one check
 * @param precheck
 */
export function isValidCompletedPrecheck(precheck: Precheck): boolean {
    if (precheck.status !== UpgradeCheckStatus.FINALIZED && precheck.status !== UpgradeCheckStatus.COMPLETE) {
        return false;
    }

    // Precheck jobs that actually run will have at least one check
    // Precheck jobs that failed due to an error will not have any
    return precheck.checks.length !== 0;
}

export function getEdgeServiceStatus(
    appliance: CduAppliance,
    release: Release,
    secondaryAppliance: CduAppliance | null = null,
): EdgeStatusMessageWithIcon {
    if (!release?.emsSupported) {
        return { message: 'Not eligible', icon: 'ban-circle' };
    }

    if (secondaryAppliance) {
        if (
            !isSSUEligibleActiveCluster(appliance, secondaryAppliance) &&
            appliance.lastWorkflowStatus !== WorkflowInstanceStatus.ACTIVE &&
            secondaryAppliance.lastWorkflowStatus !== WorkflowInstanceStatus.ACTIVE
        ) {
            return { message: 'Unsupported ActiveCluster configuration', icon: 'ban-circle' };
        }
    }

    if (appliance?.edgeStatus) {
        if (appliance.edgeStatus === EdgeStatus.CONNECTED) {
            if (!noUnsupportedReplicationsExistOnAppliance(appliance) && !secondaryAppliance) {
                return {
                    message: 'Not eligible - ' + getUnsupportedReplicationNamesPresentOnAppliance(appliance),
                    icon: 'ban-circle',
                };
            }
            if (appliance.activeClusterStatus === ActiveClusterStatus.PAIRED_ARRAY_NOT_VISIBLE) {
                return { message: 'Unsupported ActiveCluster configuration', icon: 'ban-circle' };
            }
            return {
                message: EdgeStatusLabel[appliance.edgeStatus],
                icon: getEdgeServiceStatusIcon(appliance.edgeStatus),
            };
        } else if (EdgeStatusLabel[appliance.edgeStatus]) {
            return {
                message: EdgeStatusLabel[appliance.edgeStatus],
                icon: getEdgeServiceStatusIcon(appliance.edgeStatus),
            };
        } else {
            console.warn('Unrecognized EdgeStatus', appliance.edgeStatus);
        }
    }

    return { message: 'Not Configured', icon: 'ban-circle' };
}

function getEdgeServiceStatusIcon(edgeStatus: EdgeStatus): string {
    if (edgeStatus === EdgeStatus.CONNECTED) {
        return 'edge-service-check';
    }
    if ([EdgeStatus.AGENT_NEEDS_UPDATE, EdgeStatus.AGENT_NOT_INSTALLED].includes(edgeStatus)) {
        return 'edge-service-status-minus';
    }
    return 'ban-circle';
}

export function isCheckOverriden(check: Check, instance: InstallUpgradeWorkflowInstance): boolean {
    if (check.status === CheckStatus.OVERRIDDEN) {
        return true;
    }

    if (!check.overridable || check.status === CheckStatus.PASSED) {
        return false;
    }

    // Only InstallChecks have check list id
    const { checkListId } = check as InstallCheck;
    if (!checkListId || !instance) {
        return false;
    }

    return instance.checkOverrides.some(override => {
        // Order checklists descending (without array mutation)
        const checkLists = instance.checkLists.slice().sort((a, b) => b.createTime.diff(a.createTime));
        const checkList = checkLists.find(x => x.id === checkListId);

        if (!checkList) {
            return false;
        }

        if (override.name !== check.name) {
            return false;
        }

        const checkListIndex = checkLists.indexOf(checkList);

        // If this is the last checklist then the override can belong to this check
        // Also if the checklist after this one is before this override then this check could be overriden
        if (checkListIndex !== 0 && override.createTime.isAfter(checkLists[checkListIndex - 1].createTime)) {
            return false;
        }

        if (override.durability === InstallCheckOverrideDurability.CHECK_STEP) {
            return checkList.checkStep === override.checkStep;
        }

        if (override.durability === InstallCheckOverrideDurability.STEP) {
            return checkList.checkStep === override.checkStep && override.hopVersion === checkList.hopVersion;
        }

        if (override.durability === InstallCheckOverrideDurability.HOP) {
            return override.hopVersion === checkList.hopVersion;
        }

        return override.durability === InstallCheckOverrideDurability.INSTALLATION;
    });
}

export function getApplianceTargetVersion(appliance: CduAppliance): string {
    if (hadValidContractStatus(appliance)) {
        if (HEALTCHECK_STATUSES.includes(appliance?.status.status)) {
            return appliance.precheckTargetVersion;
        }

        return appliance?.targetVersion;
    }
    return null;
}

export function getAngularticsEventName(value: string): string {
    return `${PURITY_UPGRADES_ANALYTICS_CATEGORY} - ${value}`;
}

export function getStatus(appliance: CduAppliance, secondaryAppliance: CduAppliance = null): ArrayCurrentStatus {
    const getPartialStatus = (): Partial<ArrayCurrentStatus> => {
        switch (appliance.status.status) {
            case ApplianceUpgradeStatus.SUPPORT_CONTRACT_EXPIRED:
                return {
                    status: 'Support Contract Expired',
                    icon: 'contract-expired',
                    shouldHideToast: true,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.NOT_PHONING_HOME:
                return {
                    status: 'Not phoning home',
                    icon: 'exclamation',
                    shouldHideToast: true,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.UPGRADE_SCHEDULED:
                return {
                    icon: 'scheduled',
                    status: 'Upgrade scheduled',
                    actionRequired: ACTION_REQUIRED_OPTIONS.SUPPORT_CASE,
                    showScheduledUpgrade: true,
                    shouldHideToast: true,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.INELIGIBLE_FOR_SELF_SERVICE:
                return {
                    icon: 'error',
                    shouldHideToast: true,
                    status: 'Ineligible for self-service',
                    actionRequired: ACTION_REQUIRED_OPTIONS.SCHEDULE_ASSISTED_UPGRADE,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.EDGE_NOT_CONFIGURED:
                return {
                    status: 'PES not enabled',
                    icon: 'cloud',
                    shouldHideToast: true,
                    actionRequired: ACTION_REQUIRED_OPTIONS.ENABLE_PES,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.EDGE_NOT_CONNECTED:
                return {
                    status: 'Missing PES Connection',
                    icon: 'exclamation',
                    shouldHideToast: true,
                    actionRequired: ACTION_REQUIRED_OPTIONS.CREATE_SUPPORT_CASE,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.EDGE_NOT_ENABLED:
                return {
                    status: 'PES not enabled',
                    icon: 'cloud',
                    shouldHideToast: true,
                    actionRequired: ACTION_REQUIRED_OPTIONS.ENABLE_PES,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.AGENT_UPGRADE_REQUIRED:
            case ApplianceUpgradeStatus.AGENT_REQUIRED:
                return {
                    status: 'Purity//FA upgrade agent required',
                    icon: 'warning',
                    shouldHideToast: true,
                    actionRequired: ACTION_REQUIRED_OPTIONS.INSTALL_UPGRADE_AGENT,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.AGENT_INSTALLING:
                return {
                    icon: 'spinner',
                    toastClass: 'status-loading',
                    status: 'Purity//FA upgrade agent installation in progress',
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.AGENT_INSTALL_FAILED:
                return {
                    status: 'Purity//FA upgrade agent installation failed',
                    icon: 'exclamation',
                    toastClass: 'status-error',
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.AGENT_UNINSTALLING:
                return {
                    icon: 'spinner',
                    toastClass: 'status-loading',
                    status: 'Purity//FA upgrade agent uninstalling',
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.AGENT_NEEDS_UPDATE:
                return {
                    icon: 'warning',
                    shouldHideToast: true,
                    status: 'Purity//FA upgrade agent outdated',
                    actionRequired: ACTION_REQUIRED_OPTIONS.UPDATE_AGENT,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.HEALTH_CHECK_FAILED_TO_START:
                return {
                    icon: 'exclamation',
                    toastClass: 'status-error',
                    status: 'Health check failed',
                    actionRequired: ACTION_REQUIRED_OPTIONS.RUN_HEALTH_CHECK,
                    includeTargetVersion: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.HEALTH_CHECK_IN_PROGRESS:
                return {
                    icon: 'spinner',
                    toastClass: 'status-loading',
                    status: 'Health check in progress',
                    includeTargetVersion: true,
                    viewDetails: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.HEALTH_CHECK_ERROR:
                return {
                    icon: 'exclamation',
                    toastClass: 'status-error',
                    status: 'Health check error',
                    includeTargetVersion: true,
                    actionRequired: ACTION_REQUIRED_OPTIONS.VIEW_RESULTS,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.HEALTH_CHECK_WARNING:
                return {
                    icon: 'warning',
                    toastClass: 'status-warning',
                    status: `Health check warning`,
                    actionRequired: ACTION_REQUIRED_OPTIONS.VIEW_RESULTS,
                    includeTargetVersion: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.HEALTH_CHECK_FAILED:
                return {
                    icon: 'exclamation',
                    toastClass: 'status-error',
                    status: 'Health check failed',
                    actionRequired: ACTION_REQUIRED_OPTIONS.RUN_HEALTH_CHECK,
                    includeTargetVersion: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.HEALTH_CHECK_COMPLETED:
                return {
                    icon: 'check',
                    toastClass: 'status-success',
                    status: 'Health check completed',
                    actionRequired: ACTION_REQUIRED_OPTIONS.DOWNLOAD_BUNDLE,
                    includeTargetVersion: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.DOWNLOAD_IN_PROGRESS:
                return {
                    icon: 'spinner',
                    toastClass: 'status-loading',
                    status: 'Download in progress',
                    includeTargetVersion: true,
                    viewDetails: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.DOWNLOAD_FAILED:
                return {
                    icon: 'exclamation',
                    toastClass: 'status-error',
                    status: 'Download failed',
                    actionRequired: ACTION_REQUIRED_OPTIONS.DOWNLOAD_BUNDLE,
                    includeTargetVersion: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.DOWNLOAD_CANCELED:
                return {
                    icon: 'error',
                    toastClass: 'status-error',
                    status: 'Download canceled',
                    actionRequired: ACTION_REQUIRED_OPTIONS.DOWNLOAD_BUNDLE,
                    includeTargetVersion: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.DOWNLOAD_EXPIRED:
                return {
                    icon: 'clock',
                    status: 'Download expired',
                    actionRequired: ACTION_REQUIRED_OPTIONS.DOWNLOAD_BUNDLE,
                    includeTargetVersion: true,
                    toastClass: 'status-warning',
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.DOWNLOAD_COMPLETED:
                return {
                    icon: 'check',
                    toastClass: 'status-success',
                    status: `Download completed`,
                    actionRequired: ACTION_REQUIRED_OPTIONS.INSTALL_PURITY,
                    includeTargetVersion: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.INSTALLATION_IN_PROGRESS:
                return {
                    status: 'Installation in progress',
                    icon: 'spinner',
                    toastClass: 'status-loading',
                    includeTargetVersion: true,
                    actionRequired: ACTION_REQUIRED_OPTIONS.VIEW_DETAILS,
                    viewDetails: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.INSTALLATION_PRECHECKS_WARNING:
                return {
                    status: 'Installation pre-checks warning',
                    icon: 'paused',
                    toastClass: 'status-warning',
                    actionRequired: ACTION_REQUIRED_OPTIONS.REVIEW_TO_CONTINUE,
                    includeTargetVersion: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.INSTALLATION_PRECHECKS_FAILED:
                return {
                    includeTargetVersion: true,
                    status: 'Installation pre-checks failed',
                    icon: 'error',
                    toastClass: 'status-error',
                    actionRequired: ACTION_REQUIRED_OPTIONS.VIEW_DETAILS,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.INSTALLATION_ON_SECONDARY_CONTROLLER_COMPLETED:
                return {
                    status: 'Installation on secondary controller completed',
                    icon: 'paused',
                    toastClass: 'status-warning',
                    includeTargetVersion: true,
                    actionRequired: ACTION_REQUIRED_OPTIONS.REVIEW_TO_CONTINUE,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.INSTALLATION_MIDCHECKS_WARNING:
                return {
                    status: 'Installation mid-checks warning',
                    icon: 'paused',
                    toastClass: 'status-warning',
                    includeTargetVersion: true,
                    actionRequired: ACTION_REQUIRED_OPTIONS.REVIEW_TO_CONTINUE,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.INSTALLATION_MIDCHECKS_FAILED:
                return {
                    status: 'Installation mid-checks failed',
                    includeTargetVersion: true,
                    icon: 'error',
                    toastClass: 'status-error',
                    actionRequired: ACTION_REQUIRED_OPTIONS.VIEW_DETAILS,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.INSTALLATION_BEING_CANCELED:
                return {
                    status: 'Installation is being canceled',
                    icon: 'spinner',
                    toastClass: 'status-error',
                    includeTargetVersion: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.INSTALLATION_CANCELED:
                return {
                    icon: 'error',
                    toastClass: 'status-error',
                    status: `Installation canceled`,
                    actionRequired: ACTION_REQUIRED_OPTIONS.VIEW_REPORT,
                    includeTargetVersion: true,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.INSTALLATION_FAILED:
                return {
                    icon: 'exclamation',
                    toastClass: 'status-error',
                    status: `Installation failed`,
                    actionRequired: ACTION_REQUIRED_OPTIONS.SUPPORT_CASE,
                    includeTargetVersion: true,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.INSTALLATION_PENDING:
                return {
                    status: 'Pending installation',
                    icon: 'clock',
                    includeTargetVersion: true,
                    actionRequired: ACTION_REQUIRED_OPTIONS.VIEW_DETAILS,
                    initialView: ApplianceOverviewView.PURITY_UPGRADE,
                };

            case ApplianceUpgradeStatus.UPGRADE_COMPLETED:
                return {
                    icon: 'check',
                    toastClass: 'status-success',
                    status: `Upgrade completed`,
                    actionRequired: ACTION_REQUIRED_OPTIONS.VIEW_REPORT,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.READY_FOR_SELF_SERVICE_UPGRADE:
                return {
                    icon: 'info',
                    toastClass: 'status-success',
                    status: `Ready for self-service upgrade`,
                    actionRequired: ACTION_REQUIRED_OPTIONS.RUN_HEALTH_CHECK,
                    includeTargetVersion: false,
                    shouldHideToast: true,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            case ApplianceUpgradeStatus.UP_TO_DATE:
                return {
                    icon: 'check',
                    toastClass: 'status-success',
                    status: `Up-to-date`,
                    includeTargetVersion: false,
                    shouldHideToast: true,
                    initialView: ApplianceOverviewView.OVERVIEW,
                };

            default:
                return null;
        }
    };

    const partialStatus = appliance.status ? getPartialStatus() : null;

    if (!partialStatus) {
        return null;
    }

    const status: ArrayCurrentStatus = {
        ...partialStatus,
        applianceId: appliance.applianceId,
        timestamp: appliance.statusTime,
        showCaseNumber: partialStatus.actionRequired === ACTION_REQUIRED_OPTIONS.SUPPORT_CASE,
        isError: partialStatus.icon === 'exclamation',
    };

    if (secondaryAppliance && status.actionRequired) {
        const secondaryActionRequired = getStatus(secondaryAppliance)?.actionRequired;
        status.actionRequired = secondaryActionRequired === status.actionRequired ? status.actionRequired : null;
    }

    return status;
}

export function isHealthCheckRunning(precheck: Precheck): boolean {
    return (
        [UpgradeCheckStatus.QUEUED, UpgradeCheckStatus.REGISTERED, UpgradeCheckStatus.REQUESTED].includes(
            precheck.status,
        ) && !(moment().diff(moment(precheck.lastUpdateTime), 'hour') > 1)
    );
}

export function showSafeModeOptOut(
    safeModeBoundaryVersion: Release,
    currentVersion: Release,
    targetVersion: Release,
): boolean {
    // show the safemode auto-on modal if you're upgrading to a version that is bigger or equal than boundary version and from a version that is smaller than the boundary version
    if (!safeModeBoundaryVersion || !currentVersion || !targetVersion) {
        return false;
    }
    return Boolean(
        compareReleases(targetVersion, safeModeBoundaryVersion) < 1 &&
            compareReleases(currentVersion, safeModeBoundaryVersion) > 0,
    );
}

export function getAllApplianceVersions(
    versionPolicies: { [key: string]: PolicyVersionsModel },
    arrayId: string,
): string[] {
    if (versionPolicies[arrayId]) {
        return [
            ...versionPolicies[arrayId].inPolicyVersions.map(x => x.version),
            ...versionPolicies[arrayId].outOfPolicyVersions.map(x => x.version),
        ];
    }
    return [];
}

export function getDropdownPolicyVersionData(
    versionPolicies: { [key: string]: PolicyVersionsModel },
    appliance: CduAppliance,
): versionsDropdownItem[] {
    const applianceId = appliance.applianceId;

    if (!versionPolicies?.[applianceId]) {
        return [];
    }
    return [
        ...getExpandableDropdownPolicyVersionDataBasedOnInPolicy(versionPolicies[applianceId].inPolicyVersions, true),
        ...getExpandableDropdownPolicyVersionDataBasedOnInPolicy(
            versionPolicies[applianceId].outOfPolicyVersions,
            false,
        ),
    ]
        .sort(compareExpandableDropdownVersionData)
        .filter(
            x => x.value !== '6.5.2' || !['FA-m10', 'FA-m20', 'FA-m50', 'FA-m70'].includes(appliance.hardwareModel),
        );
}

function getExpandableDropdownPolicyVersionDataBasedOnInPolicy(
    policyVersionData: PolicyVersionData[],
    inPolicy: boolean,
): versionsDropdownItem[] {
    return policyVersionData.map(policyVersionItem => {
        return {
            value: policyVersionItem.version,
            label: getPolicyVersionText(policyVersionItem),
            outOfPolicy: !inPolicy,
        };
    });
}

function compareExpandableDropdownVersionData(version1: versionsDropdownItem, version2: versionsDropdownItem): number {
    if (version1.outOfPolicy !== version2.outOfPolicy) {
        return version1.outOfPolicy ? 1 : -1;
    }
    return compareVersionStrings(version1.value, version2.value);
}

/**
 * Compares two version strings (e.g., "6.8.1" vs "6.10.1") and returns a numeric value indicating their order.
 * The function splits the version strings by '.' or 'rc', compares each numeric part in order.
 *
 * This is useful for sorting or comparing software versions, and avoids issues with string comparison
 * (e.g., "6.10.1" is correctly considered greater than "6.8.1").
 *
 * @param version1 The first version string to compare.
 * @param version2 The second version string to compare.
 * @returns A negative number if version1 > version2, positive if version1 < version2, or 0 if equal.
 */
export function compareVersionStrings(version1: string, version2: string): number {
    const splitVersion1 = version1.split(/\.|rc/);
    const splitVersion2 = version2.split(/\.|rc/);

    if (splitVersion1.length !== splitVersion2.length) {
        return splitVersion1.length - splitVersion2.length;
    }

    for (let i = 0; i < splitVersion1.length; i++) {
        const diff = Number(splitVersion2[i]) - Number(splitVersion1[i]);
        if (diff) return diff;
    }

    return 0;
}

function getLifecycleAndSupportStatesText(lifecycleState: LifecycleState, supportState: SupportState): string {
    return lifecycleState && supportState ? `(${lifecycleState}/${SupportStateShortcut[supportState]})` : '';
}

function getVersionTextWithLifecycleAndSupportStates(
    version: string,
    lifecycleState: LifecycleState,
    supportState: SupportState,
): string {
    const lifecycleAndSupportStatesText = getLifecycleAndSupportStatesText(lifecycleState, supportState);
    const additionalText = lifecycleAndSupportStatesText ? ` ${lifecycleAndSupportStatesText}` : '';
    return `${version}${additionalText}`;
}

function getPolicyVersionText(policyVersionItem: PolicyVersionData): string {
    return getVersionTextWithLifecycleAndSupportStates(
        policyVersionItem.version,
        policyVersionItem.lifecycleState,
        policyVersionItem.supportState,
    );
}

export function getFormattedReleasesTextForVersion(version: string, releases: { [key: string]: Release }): string {
    const release = releases[version];
    return getVersionTextWithLifecycleAndSupportStates(version, release?.lifecycleState, release?.supportState);
}

export function getFormattedReleasesTextWithoutVersion(release: Release): string {
    return getLifecycleAndSupportStatesText(release?.lifecycleState, release?.supportState);
}

export function getReleaseNotesUrl(release: Release): string {
    if (!release) {
        return DEFAULT_RELEASE_NOTES_URL;
    }

    if (!release.releaseNotesUrl) {
        return DEFAULT_RELEASE_NOTES_URL;
    }

    return release.releaseNotesUrl;
}

export function getLatestRelease(versions: string[], releasesMap: { [key: string]: Release }): Release {
    if (!versions || !releasesMap) {
        return null;
    }
    return (
        versions
            .map(x => releasesMap[x])
            .filter(x => x)
            .sort(compareReleases)[0] || null
    );
}

export function sortPrechecksByTime(
    first: CloudPrecheck | OnArrayPrecheckJob,
    second: CloudPrecheck | OnArrayPrecheckJob,
): number {
    let firstTime = first?.checkTime || first?.createTime;
    let secondTime = second?.checkTime || second?.createTime;
    if (!firstTime || !secondTime) {
        return 0;
    }
    if (typeof firstTime === 'string') {
        firstTime = moment(firstTime);
    }
    if (typeof secondTime === 'string') {
        secondTime = moment(secondTime);
    }
    return secondTime.diff(firstTime);
}

export function isSSUEligibleActiveCluster(appliance: CduAppliance, secondaryAppliance: CduAppliance): boolean {
    // checks if the appliances are in an active cluster pod that is SSU eligible
    if (appliance.currentVersion !== secondaryAppliance.currentVersion) {
        return false;
    }
    if (appliance.policy !== secondaryAppliance.policy) {
        return false;
    }
    if (
        appliance.activeClusterStatus !== ActiveClusterStatus.SYNCED ||
        secondaryAppliance.activeClusterStatus !== ActiveClusterStatus.SYNCED
    ) {
        return false;
    }
    if (!appliance.activeClusterTargetAplianceIds.includes(secondaryAppliance.applianceId)) {
        return false;
    }
    return secondaryAppliance.activeClusterTargetAplianceIds.includes(appliance.applianceId);
}

export function isPDUScheduled(appliance: CduAppliance): boolean {
    return Boolean(appliance?.assistedUpgradeCaseId && appliance?.assistedUpgradeCaseNumber);
}

export function updateActiveClusterPairs(appliances: CduAppliance[]): { [key: string]: CduAppliance } {
    const activeClusterPairs = {};
    appliances.forEach(appliance => {
        if (!appliance.activeClusterTargetAplianceIds || appliance.activeClusterTargetAplianceIds.length !== 1) {
            return;
        }
        const activeClusterPairId = appliance.activeClusterTargetAplianceIds[0];
        const activeClusterPair = appliances.find(
            secondaryAppliance => secondaryAppliance.applianceId === activeClusterPairId,
        );
        if (activeClusterPair) {
            activeClusterPairs[appliance.applianceId] = activeClusterPair;
        }
    });
    return activeClusterPairs;
}

export function getApplianceAuditLogURL(workflowInstance: UpgradeWorkflowInstance): string {
    const filter = encodeURIComponent(
        JSON.stringify([
            {
                entity: 'arrays',
                key: 'array_name',
                value: workflowInstance.arrayHost,
            },
        ]),
    );

    const timeRange = encodeURIComponent(
        JSON.stringify([
            {
                key: 'startTime',
                value: workflowInstance.createTime.valueOf().toString(),
            },
            {
                key: 'endTime',
                value: workflowInstance.lastUpdateTime.valueOf().toString(),
            },
        ]),
    );

    return `/messages/auditlog/appliance?filter=${filter}&timeRange=${timeRange}`;
}

export function getUpgradeTimeEstimationText(
    workflowStateInstance: UpgradeWorkflowInstance,
    secondaryWorkflowStateInstance: UpgradeWorkflowInstance = null,
): string {
    const hops = workflowStateInstance.upgradePath ? workflowStateInstance.upgradePath.length - 1 : 1;
    let applianceLabel = 'appliance';
    const hopText = hops > 1 ? 'multi-hop' : 'single-hop';
    let estimatedHours = hops;

    if (secondaryWorkflowStateInstance) {
        applianceLabel = 'ActiveCluster';
        estimatedHours = estimatedHours * 2;
    }

    return `The estimated time for your current ${hopText} ${applianceLabel} upgrade installation is ${estimatedHours} ${estimatedHours > 1 ? 'hours' : 'hour'}.`;
}

export function getArrayUniqueName(array: PartialDeep<CduAppliance>, hasDuplicateApplianceNames: boolean): string {
    return array.hostname + (hasDuplicateApplianceNames ? '.' + array.domain : '');
}

export function isApplianceRunningWorkflow(appliance: CduAppliance): boolean {
    return appliance.lastWorkflowStatus === WorkflowInstanceStatus.ACTIVE;
}

export function isApplianceRunningPatchWorkflow(appliance: CduAppliance): boolean {
    return (
        appliance.lastWorkflowStatus === WorkflowInstanceStatus.ACTIVE &&
        appliance.lastWorkflowName === WorkflowCreateRequestType.PATCH
    );
}

export function isApplianceRunningHealtCheck(appliance: CduAppliance): boolean {
    return HEALTHCHECK_IN_PROGRESS_STATUSES.includes(appliance.status.status);
}

export function hadApplianceUpgradeVersions(
    appliance: CduAppliance,
    versionPolicies: { [key: string]: PolicyVersionsModel },
    releases: Release[],
): boolean {
    if (versionPolicies[appliance.applianceId]) {
        const allPolicyVersions = getAllApplianceVersions(versionPolicies, appliance.applianceId);
        return (
            allPolicyVersions.length !== 0 &&
            allPolicyVersions.filter(v => releases.find(r => r.versionFull === v)).length !== 0
        );
    }
    return false;
}

export function hadApplianceDownloadBundle(appliance: CduAppliance): boolean {
    return appliance.status.status === ApplianceUpgradeStatus.DOWNLOAD_COMPLETED;
}

export function hadValidContractStatus(appliance: CduAppliance): boolean {
    return appliance.contractStatus !== ContractStatus.EXPIRED;
}

export function isApplianceFlashArray(appliance: CduAppliance): boolean {
    return [
        ApplianceSotwareName.PurityFAAWS,
        ApplianceSotwareName.PurityFAAZ,
        ApplianceSotwareName.PurityFANE,
        ApplianceSotwareName.PurityFA,
    ].includes(appliance.softwareName);
}

export function isApplianceFlashBlade(appliance: CduAppliance): boolean {
    return appliance.softwareName === ApplianceSotwareName.PurityFB;
}

export function getReleaseTypeBySoftwareName(softwareName: ApplianceSotwareName): SoftwareType {
    const releasesBySoftwareType = {
        [ApplianceSotwareName.PurityFA]: SoftwareType.PurityFA,
        [ApplianceSotwareName.PurityFANE]: SoftwareType.PurityFANE,
        [ApplianceSotwareName.PurityFB]: SoftwareType.PurityFB,
        [ApplianceSotwareName.PurityFAAWS]: SoftwareType.PurityFAAws,
        [ApplianceSotwareName.PurityFAAZ]: SoftwareType.PurityFAAzure,
    };
    return releasesBySoftwareType[softwareName] || SoftwareType.PurityFA;
}

export function getUpgradePathRequiredInfoFromAppliance(appliance: CduAppliance): UpgradePathRequiredInfo {
    return {
        applianceId: appliance.applianceId,
        currentVersion: appliance.currentVersion,
        targetVersion: appliance.targetVersion,
        controllerModel: appliance.hardwareModel,
        softwareName: appliance.softwareName,
    };
}

export function convertWorkflowInstanceToPrecheck(instance: UpgradeWorkflowInstance, applianceId: string): Precheck {
    return {
        succeeded: false,
        arrayId: applianceId,
        id: instance.cloudPrecheckId,
        lastUpdatedBy: instance.createdBy,
        lastUpdateTime: instance.lastUpdateTime,
        createdBy: instance.createdBy,
        createTime: instance.createTime,
        checkTime: null,
        groupId: null,
        status: UpgradeCheckStatus.REQUESTED,
        startVersion: instance.startVersion,
        targetVersion: instance.targetVersion,
        uiName: CloudPrecheckUiName.SSU,
        upgradePath: instance.upgradePath,
        checks: [],
    };
}

function getApplianceById(appliances: CduAppliance[], applianceId: string): CduAppliance {
    return appliances.find(a => a.applianceId === applianceId);
}

function isApplianceAfter(appliances: CduAppliance[], appliance1: CduAppliance, appliance2: CduAppliance): boolean {
    const applianceIds = appliances.map(a => a.applianceId);
    return applianceIds.indexOf(appliance1?.applianceId) > applianceIds.indexOf(appliance2?.applianceId);
}

export function getReplicationAppliance(
    appliances: CduAppliance[],
    activeClusterPairs: { [key: string]: CduAppliance },
    applianceId: string,
): CduAppliance {
    const appliance = getApplianceById(appliances, applianceId);
    if (!appliance) {
        return null;
    }

    return activeClusterPairs[appliance.applianceId] || null;
}

export function isReplicationAppliancePrimary(
    appliances: CduAppliance[],
    activeClusterPairs: { [key: string]: CduAppliance },
    applianceId: string,
): boolean {
    const appliance = getApplianceById(appliances, applianceId);
    if (!appliance) {
        return null;
    }

    return (
        isApplianceReplicated(appliances, activeClusterPairs, applianceId) &&
        isApplianceAfter(
            appliances,
            getReplicationAppliance(appliances, activeClusterPairs, appliance.applianceId),
            appliance,
        )
    );
}

export function isReplicationApplianceSecondary(
    appliances: CduAppliance[],
    activeClusterPairs: { [key: string]: CduAppliance },
    applianceId: string,
): boolean {
    const appliance = getApplianceById(appliances, applianceId);
    if (!appliance) {
        return null;
    }

    return (
        isApplianceReplicated(appliances, activeClusterPairs, applianceId) &&
        isApplianceAfter(
            appliances,
            appliance,
            getReplicationAppliance(appliances, activeClusterPairs, appliance.applianceId),
        )
    );
}

export function isApplianceReplicated(
    appliances: CduAppliance[],
    activeClusterPairs: { [key: string]: CduAppliance },
    applianceId: string,
): boolean {
    const appliance = getApplianceById(appliances, applianceId);
    if (!appliance) {
        return false;
    }

    const replicatedAppliance = getReplicationAppliance(appliances, activeClusterPairs, appliance.applianceId);

    if (replicatedAppliance) {
        const replicatedCheck = getReplicationAppliance(
            appliances,
            activeClusterPairs,
            replicatedAppliance.applianceId,
        );
        if (replicatedCheck) {
            if (replicatedCheck.applianceId === applianceId) {
                return true;
            }
        }
    }

    return false;
}

export function isReplicatedApplianceNextToOther(
    appliances: CduAppliance[],
    activeClusterPairs: { [key: string]: CduAppliance },
    applianceId: string,
): boolean {
    const appliance = getApplianceById(appliances, applianceId);
    const replicatedAppliance = getReplicationAppliance(appliances, activeClusterPairs, applianceId);
    if (replicatedAppliance) {
        const applianceIds = appliances.map(a => a.applianceId);
        return (
            Math.abs(
                applianceIds.indexOf(appliance.applianceId) - applianceIds.indexOf(replicatedAppliance.applianceId),
            ) === 1
        );
    }

    return false;
}

export function getAutoContinueValues(pauseAfterSecondaryUpgrade: boolean): autoContinues[] {
    return pauseAfterSecondaryUpgrade
        ? [autoContinues.AFTER_PRE_CHECK, autoContinues.AFTER_POST_CHECK]
        : [autoContinues.AFTER_PRE_CHECK, autoContinues.AFTER_MID_CHECK, autoContinues.AFTER_POST_CHECK];
}

export function formatReleasesBySoftwareType(releases: Release[]): {
    [key in SoftwareType]: { [key: string]: Release };
} {
    return releases.sort(compareReleases).reduce(
        (acc, release) => {
            if (acc[release.softwareType]) {
                acc[release.softwareType][release.versionFull] = release;
            }
            return acc;
        },
        {
            [SoftwareType.PurityFA]: {},
            [SoftwareType.PurityFANE]: {},
            [SoftwareType.PurityFB]: {},
            [SoftwareType.PurityFAAws]: {},
            [SoftwareType.PurityFAAzure]: {},
        },
    );
}

export function openEmsInstructionsModal(appliance: CduAppliance, modalService: NgbModal): void {
    const modal = modalService.open(EnableDisableEdgeServiceModalComponent, {
        windowClass: 'ems-modal',
        backdrop: 'static',
    });
    modal.componentInstance.activeModal = modal;
    modal.componentInstance.arrayName = appliance.hostname;
}
